/**
 * Profile Complete Modal
 * 
 * Shown after first magic link login to capture user's display name.
 * Only appears once - name is stored in Supabase user_metadata.
 * Also assigns the user's club on completion (for non-Lumen8 users).
 */

import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";

// ============================================================
// STYLES
// ============================================================
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    borderRadius: 20,
    padding: "40px 48px",
    maxWidth: 420,
    width: "90%",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  glow: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: 200,
    height: 200,
    background: "radial-gradient(circle, rgba(47, 214, 181, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  emoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 8px",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  subtitle: {
    fontSize: 14,
    color: "rgba(255, 255, 255, 0.6)",
    margin: "0 0 28px",
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  inputWrapper: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    color: "#fff",
    fontSize: 16,
    outline: "none",
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  inputFocus: {
    borderColor: "#2fd6b5",
    backgroundColor: "rgba(47, 214, 181, 0.05)",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.15)",
  },
  button: {
    padding: "14px 24px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    color: "#000",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  buttonHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(47, 214, 181, 0.3)",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    transform: "none",
  },
  error: {
    color: "#f87171",
    fontSize: 13,
    marginTop: 8,
    textAlign: "left",
  },
  hint: {
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.4)",
    marginTop: 16,
  },
};

// ============================================================
// PROFILE COMPLETE MODAL COMPONENT
// ============================================================
export function ProfileCompleteModal({ onComplete }) {
  const { updateUserName, user, assignUserClub } = useAuth();
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please enter your name");
      return;
    }

    if (trimmedName.length < 2) {
      setError("Name must be at least 2 characters");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await updateUserName(trimmedName);
      
      // Assign club to user (only for non-Lumen8 users)
      if (assignUserClub) {
        await assignUserClub();
      }
      
      // Call onComplete callback if provided
      if (onComplete) {
        await onComplete();
      }
      
      // Modal will automatically hide because needsProfileComplete will become false
    } catch (err) {
      console.error("Error updating name:", err);
      setError("Failed to save. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.glow} />
        
        <div style={styles.emoji}>👋</div>
        
        <h2 style={styles.title}>Welcome to VenueFlow!</h2>
        <p style={styles.subtitle}>
          Let's get you set up. What should we call you?
        </p>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputWrapper}>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Your name"
              style={{
                ...styles.input,
                ...(isFocused ? styles.inputFocus : {}),
              }}
              autoFocus
              disabled={isSubmitting}
            />
            {error && <div style={styles.error}>{error}</div>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !name.trim()}
            style={{
              ...styles.button,
              ...(isHovered && !isSubmitting ? styles.buttonHover : {}),
              ...(isSubmitting || !name.trim() ? styles.buttonDisabled : {}),
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isSubmitting ? "Saving..." : "Continue →"}
          </button>
        </form>

        <p style={styles.hint}>
          Signed in as {user?.email}
        </p>
      </div>
    </div>
  );
}

export default ProfileCompleteModal;
